/* Native details/links/forms are the baseline; this adds desktop coordination and modal mobile behavior. */
if (!customElements.get('duryn-navigation')) {
  customElements.define('duryn-navigation', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this.controller) this.init(); });
    }

    init() {
      this.mobile = this.querySelector('[data-mobile-navigation]');
      if (!this.mobile) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.disclosures = [...this.querySelectorAll('[data-nav-disclosure]')];
      this.inertState = new Map();
      this.mobileQuery = matchMedia('(max-width: 999px)');
      this.hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
      this.mobileOpen = false;
      this.dataset.enhanced = 'true';
      if (this.hasAttribute('data-transparent-home')) {
        this.updateScrollState = () => {
          // Mobile drawer scroll-lock resets window.scrollY; retain the underlying page state.
          if (!this.mobileOpen) this.dataset.scrolled = String(window.scrollY > 8);
        };
        window.addEventListener('scroll', this.updateScrollState, { ...options, passive: true });
        window.addEventListener('pageshow', this.updateScrollState, options);
        this.updateScrollState();
      }
      this.toggleButton = this.mobile.querySelector(':scope > summary');

      this.disclosures.forEach(details => {
        details.addEventListener('toggle', () => {
          if (details.open) {
            this.closeDesktop(details);
            this.querySelectorAll('.dn-tools [data-localization][open]').forEach(item => { item.open = false; });
          }
        }, options);
        details.querySelector('summary').addEventListener('pointerenter', () => {
          if (!this.hoverQuery.matches || this.mobileQuery.matches) return;
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => { this.closeDesktop(details); details.open = true; }, 120);
        }, options);
        details.addEventListener('pointerenter', () => clearTimeout(this.leaveTimer), options);
        details.addEventListener('pointerleave', () => {
          clearTimeout(this.hoverTimer);
          if (!this.hoverQuery.matches) return;
          this.leaveTimer = setTimeout(() => {
            if (!details.contains(document.activeElement)) details.open = false;
          }, 220);
        }, options);
        details.addEventListener('focusout', () => {
          queueMicrotask(() => {
            if (this.isConnected && !details.contains(document.activeElement)) details.open = false;
          });
        }, options);
      });
      this.mobile.addEventListener('toggle', () => this.syncMobile(), options);
      this.querySelector('[data-close-navigation]').addEventListener('click', () => this.closeMobile(), options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.closeDesktop();
        document.querySelectorAll('.dn [data-localization][open]').forEach(details => {
          if (!details.contains(event.target)) details.open = false;
        });
      }, options);
      this.querySelectorAll('.dn-tools [data-localization]').forEach(details => {
        details.addEventListener('toggle', () => { if (details.open) this.closeDesktop(); }, options);
      });
      this.querySelector('.dn-tools').addEventListener('click', event => {
        if (!event.target.closest('[data-localization]')) this.closeDesktop();
      }, options);
      document.addEventListener('keydown', event => this.onKeydown(event), options);
      this.mobile.addEventListener('click', event => {
        const link = event.target.closest('a[href]');
        if (!link) return;
        this.closeMobile(false);
        if (link.hasAttribute('data-cart-proxy') && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
          const cart = this.querySelector('cart-drawer-component');
          if (typeof cart?.open === 'function') { event.preventDefault(); cart.open(); }
        }
      }, options);
      this.mobileQuery.addEventListener('change', () => {
        this.closeMobile(false);
        this.closeDesktop();
        this.measure();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const blockId = event.detail?.blockId;
        if (!blockId) return;
        const editingSurface = this.querySelector(this.mobileQuery.matches ? '.dn-drawer' : '.dn-desktop');
        const item = [...editingSurface.querySelectorAll('[data-shopify-editor-block]')].find(node => {
          try { return JSON.parse(node.getAttribute('data-shopify-editor-block')).id === blockId; } catch { return false; }
        });
        if (!item) return;
        if (this.mobileQuery.matches) {
          this.mobile.open = true;
          this.mobile.querySelectorAll('.dn-accordion').forEach(node => { node.open = true; });
        } else {
          const owner = item.closest('[data-nav-disclosure]');
          if (owner) { this.closeDesktop(owner); owner.open = true; }
        }
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.querySelector('.dn-bar'));
      const group = document.querySelector('#header-group');
      if (group) [...group.children].forEach(node => { if (node instanceof HTMLElement) this.resizeObserver.observe(node); });
      const cartCount = this.querySelector('.cart-bubble__text-count');
      if (cartCount) {
        const copyCount = () => {
          const value = cartCount.textContent.trim();
          const mobileCount = this.querySelector('[data-mobile-cart-count]');
          mobileCount.textContent = value;
          mobileCount.hidden = !value || value === '0';
        };
        this.cartObserver = new MutationObserver(copyCount);
        this.cartObserver.observe(cartCount, { childList: true, characterData: true, subtree: true });
        copyCount();
      }
      this.measure();
      this.syncMobile();
    }

    measure() {
      const height = Math.round(this.querySelector('.dn-bar').getBoundingClientRect().height);
      if (!height) return;
      document.body.style.setProperty('--header-height', `${height}px`);
      const group = document.querySelector('#header-group');
      const total = group ? [...group.children].reduce((sum, node) => sum + (node instanceof HTMLElement ? node.getBoundingClientRect().height : 0), 0) : height;
      document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    }

    closeDesktop(except = null) {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
      this.disclosures.forEach(details => { if (details !== except) details.open = false; });
    }

    syncMobile() {
      if (!this.controller) return;
      const open = this.mobile.open && this.mobileQuery.matches;
      if (open === this.mobileOpen) return;
      this.mobileOpen = open;
      if (open) {
        this.closeDesktop();
        this.mobile.setAttribute('role', 'dialog');
        this.mobile.setAttribute('aria-modal', 'true');
        this.mobile.setAttribute('aria-label', this.querySelector('.dn-desktop').getAttribute('aria-label'));
        // Inert every sibling branch up to body, while preserving any pre-existing inert state.
        let branch = this.mobile;
        while (branch.parentElement && branch.parentElement !== document.documentElement) {
          [...branch.parentElement.children].forEach(sibling => {
            if (sibling !== branch && sibling instanceof HTMLElement && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
              this.inertState.set(sibling, sibling.inert);
              sibling.inert = true;
            }
          });
          branch = branch.parentElement;
        }
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.toggleButton.focus();
      } else {
        this.mobile.removeAttribute('role');
        this.mobile.removeAttribute('aria-modal');
        this.mobile.removeAttribute('aria-label');
        this.inertState.forEach((value, node) => { node.inert = value; });
        this.inertState.clear();
        document.body.style.overflow = this.previousOverflow || '';
      }
    }

    closeMobile(restoreFocus = true) {
      const wasOpen = this.mobile.open;
      this.mobile.open = false;
      this.syncMobile();
      if (wasOpen && restoreFocus && this.isConnected) this.toggleButton.focus();
    }

    onKeydown(event) {
      // Native cart/search/account dialogs own their Escape and focus behavior.
      if (event.target.closest('dialog[open], [popover]')) return;
      if (event.key === 'Escape') {
        const local = event.target.closest('.dn [data-localization][open]');
        if (local) { local.open = false; local.querySelector('summary').focus(); event.preventDefault(); return; }
        if (this.mobile.open) { this.closeMobile(); event.preventDefault(); return; }
        const opened = this.disclosures.find(details => details.open);
        if (opened) { this.closeDesktop(); opened.querySelector('summary').focus(); event.preventDefault(); }
      }
      if (event.key === 'ArrowDown') {
        const owner = event.target.closest('[data-nav-disclosure]');
        if (owner && event.target === owner.querySelector('summary')) {
          event.preventDefault(); this.closeDesktop(owner); owner.open = true;
          owner.querySelector('a[href]')?.focus();
        }
      }
      if (event.key === 'Tab' && this.mobileOpen) {
        const focusable = [...this.mobile.querySelectorAll('summary,a[href],button,input,select,[tabindex="0"]')].filter(node => !node.disabled && node.tabIndex !== -1 && node.getClientRects().length);
        const first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.closeMobile(false);
      this.closeDesktop();
      this.controller.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.cartObserver?.disconnect();
      delete this.dataset.enhanced;
      document.body.style.removeProperty('--header-height');
      document.body.style.removeProperty('--header-group-height');
    }
  });
}
